import { Routes, Route } from 'react-router-dom'
import { ScrollToTop } from './components/ScrollToTop'
import { Error, SharedLayout, Login } from './pages/index'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { clientRoutes } from './routes/clientRoutes'
import { adminRoutes } from './routes/adminRoutes'
import { SpinnerCondition } from './SpinnerCondition'
import useAuth from './hooks/useAuth'
import { ConfirmModal } from 'components/ConfirmModal/ConfirmModal'
import { useDispatch, useSelector } from 'react-redux'
import { setConfirmModal } from 'features/global/globalSlice'
import { AppDispatch } from 'store'
import { IRoute } from 'types/global.interface'
import { ThemeProvider, createGlobalStyle } from 'styled-components'
import { darkTheme } from 'styling/GlobalStyles'
import OverlayProvider from 'OverlayContext'
import { createPortal } from 'react-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'

const queryClient = new QueryClient()

const ToastAboveModals = createGlobalStyle`
  .Toastify,
  .Toastify__toast-container {
    z-index: 10000100 !important;
  }
`

const TOAST_ROOT_ID = 'app-toast-root'

const Home = () => {
  const { isAdmin } = useAuth()
  const { confirmModal } = useSelector((store: any) => store.globals)
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    const root = document.getElementById(TOAST_ROOT_ID)
    if (!root) return undefined
    const keepLast = () => {
      if (document.body.lastElementChild !== root) document.body.appendChild(root)
    }
    keepLast()
    const obs = new MutationObserver(keepLast)
    obs.observe(document.body, { childList: true })
    return () => obs.disconnect()
  }, [])

  const handleCancel = () => {
    dispatch(setConfirmModal({ isOpen: false }))
    confirmModal.onCancel && confirmModal.onCancel()
  }

  const renderRoutes = (routes) => {
    return routes.map((route: IRoute, index: number) => {
      return (
        <Route
          path={route.path}
          element={
            <OverlayProvider>
              <route.Component />
            </OverlayProvider>
          }
          key={index}
        />
      )
    })
  }

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ToastAboveModals />
        <SpinnerCondition />
        <ScrollToTop />
        <Routes>
          <Route path='/' element={<SharedLayout />}>
            {renderRoutes(isAdmin ? adminRoutes : clientRoutes)}
          </Route>
          <Route path='*' element={<Error />} />
        </Routes>
        {createPortal(
          <div id={TOAST_ROOT_ID}>
            <ToastContainer
              closeButton={false}
              pauseOnHover={false}
              closeOnClick={true}
              autoClose={2500}
              limit={2}
              newestOnTop
              position='top-center'
              style={{ zIndex: 10000100 }}
            />
          </div>,
          document.body,
        )}
        {confirmModal.isOpen && (
          <ConfirmModal
            word={confirmModal.word}
            onSave={confirmModal.onSave}
            onCancel={handleCancel}
            setParams={confirmModal.setParams}
            sentence={confirmModal.sentence}
            cancelText={confirmModal.cancelText}
            confirmText={confirmModal.confirmText}
          />
        )}
      </QueryClientProvider>
    </>
  )
}

const App = () => {
  const auth = useAuth()
  return (
    <ThemeProvider theme={darkTheme}>
      {auth.user ? <Home /> : <Login />}
    </ThemeProvider>
  )
}

export default App
